import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ILLUSTRATIONS } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { debounce } from "../../lib/imageOptimization";

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = debounce(() => setScrolled(window.scrollY > 24), 8);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { label: t.nav.work, href: "#portfolio" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.process, href: "#process" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-forest/10 py-4" : "py-6 border-b border-transparent"
      }`}
    >
      {/* An explicit gap rather than relying on justify-between alone: with both
          groups shrink-0, "space between" can resolve to no space at all once
          the row is full, which is how the brand ended up flush against the
          language switcher at 320px. */}
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between gap-4 md:gap-6">
        {/* Anton is wide, and at 320px the wordmark was spilling out of its own
            flex box and straight through the language switcher. Both sides are
            shrink-0 and the mark steps down with the viewport instead, so the
            row is sized to fit rather than fought over — letting the brand
            shrink just collapses it to nothing, since the text has no natural
            wrap point. */}
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 md:gap-3 group shrink-0">
          <img src={ILLUSTRATIONS.mark} alt="" loading="lazy" decoding="async" className="h-7 w-7 md:h-9 md:w-9 shrink-0 object-contain mix-blend-multiply" />
          <span className="font-display text-lg sm:text-xl md:text-2xl leading-none text-forest whitespace-nowrap">Bergamot</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="btn-underline micro text-charcoal/70 hover:text-forest transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 md:gap-5 shrink-0">
          <LanguageSwitcher tone="dark" />
          <a
            href="#contact"
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center btn-block !px-6 !py-3 bg-forest text-cream hover:bg-charcoal"
          >
            {t.nav.quote}
          </a>
        </div>
      </div>
    </header>
  );
};
